import { useTranslation } from 'react-i18next';
import {
  DIAGNOSE_DESCRIPTION_NY_SYGDOM,
  DIAGNOSE_DESCRIPTION_SAMME_SYGDOM,
  isLprHospitalChoiceCode,
  type LprHospitalChoiceCode,
  type LprIllness,
} from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { LprForloeb, LprForloebStatus, LprSksCode } from '@/types/lpr';

const ILLNESS = [DIAGNOSE_DESCRIPTION_NY_SYGDOM, DIAGNOSE_DESCRIPTION_SAMME_SYGDOM] as const;

export function HospitalReferralLpr({
  pathways,
  pathwaysLoading,
  pathwaysError,
  fritvalg,
  fritvalgLoading,
  forward,
  pathwayId,
  illness,
  hospitalChoice,
  attempted,
  onForward,
  onPathway,
  onIllness,
  onHospitalChoice,
}: {
  pathways: readonly LprForloeb[];
  pathwaysLoading: boolean;
  pathwaysError: boolean;
  fritvalg: readonly LprSksCode[];
  fritvalgLoading: boolean;
  forward: boolean;
  pathwayId: string | null;
  illness: LprIllness | null;
  hospitalChoice: LprHospitalChoiceCode | null;
  attempted: boolean;
  onForward: (forward: boolean) => void;
  onPathway: (id: string) => void;
  onIllness: (illness: LprIllness) => void;
  onHospitalChoice: (code: LprHospitalChoiceCode | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const canForward = !pathwaysLoading && !pathwaysError && pathways.length > 0;

  return (
    <div className="space-y-4">
      <label className="hv-lpr">
        <input
          type="checkbox"
          checked={forward}
          disabled={!canForward}
          onChange={(event) => onForward(event.target.checked)}
          className="mt-1 h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span>
          <b>
            {t(
              'patients.hospital_referral.lpr_forward',
              'Viderehenvisning i eksisterende LPR-forløb',
            )}
          </b>
          {pathwaysLoading && (
            <span className={cn('block', FORM_HINT_TYPE)}>
              {t('patients.hospital_referral.lpr_loading', 'LPR-forløb indlæses stadig.')}
            </span>
          )}
          {pathwaysError && (
            <span className={cn('block', FORM_ERROR_TYPE)}>
              {t(
                'patients.hospital_referral.lpr_error',
                'LPR-forløb kunne ikke hentes. Viderehenvisning kan ikke vælges.',
              )}
            </span>
          )}
          {!pathwaysLoading && !pathwaysError && pathways.length === 0 && (
            <span className={cn('block', FORM_HINT_TYPE)}>
              {t(
                'patients.hospital_referral.lpr_none',
                'Patienten har ikke et LPR-forløb. Viderehenvisning i et eksisterende forløb kan ikke vælges.',
              )}
            </span>
          )}
        </span>
      </label>

      {forward && pathways.length > 0 && (
        <div className="space-y-4">
          <div>
            <label htmlFor="referral-lpr-pathway" className={cn('block', FORM_LABEL_TYPE)}>
              {t('patients.hospital_referral.lpr_pathway', 'LPR-forløb')}
            </label>
            <select
              id="referral-lpr-pathway"
              value={pathwayId ?? ''}
              onChange={(event) => onPathway(event.target.value)}
              className={cn(
                TYPE.inputValue,
                'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ',
              )}
            >
              {pathways.map((row) => (
                <option key={row.id} value={row.id}>
                  {pathwayLabel(row, t)}
                </option>
              ))}
            </select>
          </div>
          <fieldset>
            <legend className={FORM_LABEL_TYPE}>
              {t('patients.hospital_referral.lpr_illness', 'Henvisningsdiagnose i forløbet')}
            </legend>
            <div className="mt-2 flex flex-wrap gap-4">
              {ILLNESS.map((code) => (
                <label
                  key={code}
                  className={cn('inline-flex items-center gap-2', TYPE.body, 'text-ds-text ')}
                >
                  <input
                    type="radio"
                    name="referral-lpr-illness"
                    value={code}
                    checked={illness === code}
                    onChange={() => onIllness(code)}
                    className="h-4 w-4 border-ds-field text-ds-teal focus:ring-ds-teal"
                  />
                  {illnessLabel(t, code)}
                </label>
              ))}
            </div>
            {attempted && illness === null && (
              <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
                {t(
                  'patients.hospital_referral.lpr_illness_required',
                  'Vælg om diagnosen er en ny sygdom eller den samme sygdom.',
                )}
              </p>
            )}
          </fieldset>
          <fieldset>
            <legend className={FORM_LABEL_TYPE}>
              {t('patients.hospital_referral.lpr_choice', 'Frit sygehusvalg')}
            </legend>
            <div className="mt-2 flex flex-wrap gap-4">
              <label className={cn('inline-flex items-center gap-2', TYPE.body, 'text-ds-text ')}>
                <input
                  type="radio"
                  name="referral-lpr-choice"
                  checked={hospitalChoice === null}
                  onChange={() => onHospitalChoice(null)}
                  className="h-4 w-4 border-ds-field text-ds-teal focus:ring-ds-teal"
                />
                {t('patients.hospital_referral.lpr_choice_none', 'Frit sygehusvalg gælder ikke')}
              </label>
              {fritvalg.map((row) => (
                <label
                  key={row.code}
                  className={cn('inline-flex items-center gap-2', TYPE.body, 'text-ds-text ')}
                >
                  <input
                    type="radio"
                    name="referral-lpr-choice"
                    value={row.code}
                    checked={hospitalChoice === row.code}
                    onChange={() => {
                      if (isLprHospitalChoiceCode(row.code)) onHospitalChoice(row.code);
                    }}
                    className="h-4 w-4 border-ds-field text-ds-teal focus:ring-ds-teal"
                  />
                  {`${row.code} ${i18n.language.startsWith('en') ? row.label_en : row.label_da}`}
                </label>
              ))}
            </div>
            {fritvalgLoading && (
              <p className={cn('mt-2', FORM_HINT_TYPE)}>
                {t('patients.hospital_referral.lpr_choice_loading', 'Koder for frit sygehusvalg indlæses.')}
              </p>
            )}
            {!fritvalgLoading && fritvalg.length === 0 && (
              <p className={cn('mt-2', FORM_HINT_TYPE)}>
                {t(
                  'patients.hospital_referral.lpr_choice_missing',
                  'Koderne for frit sygehusvalg kunne ikke hentes. Frit sygehusvalg udelades.',
                )}
              </p>
            )}
          </fieldset>
        </div>
      )}
    </div>
  );
}

function illnessLabel(
  t: (key: string, fallback: string) => string,
  code: LprIllness,
): string {
  switch (code) {
    case DIAGNOSE_DESCRIPTION_NY_SYGDOM:
      return t('patients.hospital_referral.lpr_illness_new', 'Ny sygdom');
    case DIAGNOSE_DESCRIPTION_SAMME_SYGDOM:
      return t('patients.hospital_referral.lpr_illness_same', 'Samme sygdom');
    default: {
      const unexpected: never = code;
      return unexpected;
    }
  }
}

function pathwayLabel(
  row: LprForloeb,
  t: (key: string, fallback: string) => string,
): string {
  const started = row.start_at.slice(0, 10);
  return `${started} · ${row.forloebslabel} · ${statusLabel(t, row.status)}`;
}

function statusLabel(
  t: (key: string, fallback: string) => string,
  status: LprForloebStatus,
): string {
  switch (status) {
    case 'draft':
      return t('patients.hospital_referral.lpr_status_draft', 'Kladde');
    case 'ready':
      return t('patients.hospital_referral.lpr_status_ready', 'Klar');
    case 'reported':
      return t('patients.hospital_referral.lpr_status_reported', 'Indberettet');
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}
